import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  InputTextarea,
  Stack,
  Text,
} from "@aksara-ui/react";
import ImageDropzone from "components/ImageDropzone";
import { InputSelect } from "components/Input";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeMedia, updateMedia } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  mediaId: string;
}

const initialValues = {
  name: "",
  link: "",
  wording: "",
  status: "",
};

const MediaFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  mediaId,
}) => {
  const isAddMode = !mediaId;
  const dispatch = useDispatch();
  const formikRef = useRef<FormikValues>() as any;

  const [status, setStatus] = useState("");
  const [filesMediaImage, setFilesMediaImage] = useState<any>([]);

  const { appUrl } = useSelector((state: any) => state.globalReducer);
  const { medias, isFormLoading } = useSelector(
    (state: any) => state.mediaReducer
  );

  const handleCloseModal = () => {
    onClose && onClose();
    setStatus("");
    setFilesMediaImage([]);
  };

  const handleSubmit = (values: any) => {
    const { name, link, wording, status } = values;
    const image =
      typeof filesMediaImage[0] === "object" ? filesMediaImage[0] : null;

    isAddMode
      ? dispatch(
          storeMedia(
            {
              name,
              link,
              image,
              wording,
              status,
            },
            handleCloseModal
          ) as any
        )
      : dispatch(
          updateMedia(
            mediaId,
            {
              name,
              link,
              image,
              wording,
              status,
            },
            handleCloseModal
          ) as any
        );
  };

  useEffect(() => {
    if (!isAddMode) {
      const mediaDetail = medias.find(
        (media: any) => media.id === parseInt(mediaId)
      );

      if (formikRef.current) {
        formikRef.current.setFieldValue("name", mediaDetail.name);
        formikRef.current.setFieldValue("link", mediaDetail.link);
        formikRef.current.setFieldValue("wording", mediaDetail.wording);
        formikRef.current.setFieldValue("status", mediaDetail.status);

        setStatus(mediaDetail.status);
        setFilesMediaImage([`${appUrl}/storage/${mediaDetail.image}`]);
      }
    }
  }, [isAddMode, medias, mediaId, appUrl]);

  const validationSchema = yup.object({
    name: yup.string().required("Name is required field"),
    link: yup.string().required("Link is required field"),
    wording: yup.string().required("Wording is required field"),
    status: yup.number().required("Status is required field"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>
                  {isAddMode ? "Add Media" : "Update Media"}
                </Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="name">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="name">Name</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="link">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="link">Link</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="wording">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="wording">Wording</FormLabel>

                        <InputTextarea
                          rows={4}
                          width="100%"
                          errors={!!meta.error && meta.touched}
                          {...field}
                        />

                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="status">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="status">Status</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputSelect
                            items={[
                              {
                                label: "Active",
                                value: 1,
                              },
                              {
                                label: "Non Active",
                                value: 0,
                              },
                            ]}
                            label="Status"
                            error={!!meta.error && meta.touched}
                            value={status}
                            handleChange={(e: any) => {
                              setStatus(e);
                              setFieldValue("status", e);
                            }}
                            boxStyle={{ width: "100%" }}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Stack spacing="xs">
                    <FormLabel>Image</FormLabel>
                    <ImageDropzone
                      files={filesMediaImage}
                      setFiles={setFilesMediaImage}
                    />
                  </Stack>
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    {isAddMode ? "Add" : "Update"}
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default MediaFormModal;
